// Desc: Admin Overview page
import Layout from "../../../components/Layout";
import PageTitle from "../../../components/PageTitle";
import { httprequest } from "../../../data/api";
import SelectGroup from "../../../components/FormComponent/SelectGroup";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import PaymentCard from "../../../components/CardCount/PaymentCard";
import { Grid } from "@mui/material";
import { Icon } from "@iconify/react/dist/iconify.js";
import Table from "../../../components/Table/Table";
import { TableSearch } from "../../../components/Table/TableActions";
import Badge from "../../../components/Badge";
import PageLoader from "../../../components/PageLoader";
import CSVExport from "../../../components/CsvExport/CsvExport";
import useWindowSize from "../../../hooks/useWindow";
import { useWard } from "../../../context/GaurdianContext";

const TERM_ENDPOINT = "/api/v1/school-plan/terms";

const GET_SESSIONS = "/api/v1/school-plan/session";
const OVERVIEW_ENDPOINT = "/api/v1/invoices/student-invoices/students";

const AdminOverview = () => {
  const navigate = useNavigate();
  const [activeSession, setActiveSession] = useState([]);
  const [allSessions, setAllSessions] = useState([]);
  const [aselectedTerm, setASelectedTerm] = useState("");
  const [searchResult, setSearchResult] = useState([]);
  // const [allInvoice, setAllInvoice] = useState([]);
  const [payemntOverview, setAllPaymentOverview] = useState([]);
  const [selectedSessionTerms, setSelectedSessionTerms] = useState([]);
  const [getIsLoading, setGetIsLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const width = useWindowSize();
  const { selectedWard } = useWard();

  // eslint-disable-next-line no-unused-vars
  const [isUploading, setIsUploading] = useState(false);

  /**
 * State for managing item form data.
 * @typedef {Object} ItemFormState
 * @property {string} status - The status of the item.
 * @property {string} fee_option - The fee option for the item.
 * @property {string} item - The item name or description.
 */

  /**
 * Determines if the current viewport is mobile sized.
 * @type {boolean}
 */
  const isMobile = width < 600;

  /**
 * Fetches all terms for a specific session.
 * @function
 * @param {string} Session_ID - The ID of the session to get terms for.
 */
  const getAllTerms = (Session_ID) => {
    httprequest
      .get(TERM_ENDPOINT)
      .then((res) => {
        setSelectedSessionTerms(res?.data?.data.filter((selected_term) => selected_term?.session_id === Session_ID));
      });
    // .catch(() => {});
  };

  /**
 * Fetches payment overview data for a specific term.
 * @function
 * @param {string|number} aselectedTerm - The ID of the term to get overview for.
 */
  const getOverview = (aselectedTerm) => {
    setGetIsLoading(true);
    httprequest
      .get(`${OVERVIEW_ENDPOINT}/${selectedWard?.id}?term_id=${aselectedTerm}`)
      .then((res) => {
        setAllPaymentOverview(res?.data?.data);
        setSearchResult(res?.data?.data?.invoices);
        setGetIsLoading(false);
      }).catch((err) => {
        setGetIsLoading(false);
        toast.error(err?.response?.message);
      });
  };

  /**
 * Fetches all sessions from the server.
 * @async
 */
  const getAllSessions = async () => {
    try {
      const res = await httprequest.get(GET_SESSIONS);
      setAllSessions(res?.data?.data);
      setActiveSession(res?.data?.data?.find((active_session) => active_session?.current_session === true));
    } catch (err) {
      toast.error(err?.response?.message);
    }
  };

  /**
 * The active term in the current session.
 * @type {Object}
 */
  const active_term_session = activeSession?.terms?.find((found_active_term) => found_active_term?.is_current === true);

  /**
 * Handles session change events.
 * @function
 * @param {Object} e - The event object from the select input.
 */
  const handleSessionChange = (e) => {
    const Session_ID = e.target.value;
    // setASelectedSession(e.target.value);
    getAllTerms(Session_ID);
  };

  /**
 * Handles term change events.
 * @function
 * @param {Object} event - The event object from the select input.
 */
  const handleTermChange = (event) => {
    const selectedTermId = event.target.value;

    setASelectedTerm(selectedTermId);
    getOverview(selectedTermId);
  };

  /**
 * Column definitions for the data table.
 * @type {Array<Object>}
 * @property {string} name - The column header name.
 * @property {Function} selector - Function to render cell content.
 * @property {boolean} [center] - Whether to center the column content.
 * @property {Function} [cell] - Alternative render function for cells.
 */
  const columns = [
    {
      name: "Item",
      selector: (row) => (
        <>
          {isMobile && <p className="mobile-styling">Item</p>}
          {row?.fee_items?.map((item) => (
            <span className="class_div" key={item?.id}>
              {item?.item}
            </span>
          )) || "--/--"}
        </>
      )
    },
    {
      name: "Invoice Name",
      selector: (row) => (
        <>
          {isMobile && <p className="mobile-styling">Invoice Name</p>}
          {row?.invoice_name || "--/--"}
        </>
      )
    },
    {
      name: "Total",
      selector: (row) => (
        <>
          {isMobile && <p className="mobile-styling">Total</p>}
          {row?.total || "--/--"}
        </>
      )
    },
    {
      name: "Due Date",
      selector: (row) => (
        <>
          {isMobile && <p className="mobile-styling">Due Date</p>}
          {row?.due_date || "--/--"}
        </>
      )
    },
    {
      name: "Status",
      center: "true",
      cell: (row) => (
        <p style={{ display: "flex", flexDirection: "column", margin: 0, marginBlock: 0 }}>
          {isMobile && <p style={{ margin: 0, fontSize: "12px" }}>Status</p>}
          <Badge status={row?.status} />
        </p>
      )
    },
    {
      name: "",
      center: "true",
      sortable: false,
      cell: (row) => (
        <div key={row?.id} className="table-action-container">
          <button className="table-action-icon" onClick={() => `${navigate(`/guardian/my-invoice/${row?.invoice_no}`)}`}>
            <Icon icon="fluent:eye-16-regular" />
          </button>
        </div>
      )
    }
  ];

  /**
 * Handles student search functionality.
 * @function
 * @param {Object} e - The event object from the search input.
 */
  const handleSearchStudent = (e) => {
    const { value } = e.target;

    const valArray = value.split(" ");

    // --| Filter data by partial match onchange in the search input box
    const result = payemntOverview?.invoices?.filter((data) => valArray?.every(
      (word) => data?.invoice_name
        ?.toLowerCase()
        .includes(word.toLowerCase()) ||
                          data?.payment_method
                            ?.toLowerCase()
                            .includes(word.toLowerCase()) ||
                          data?.amount
                            ?.toLowerCase()
                            .includes(word.toLowerCase()) ||
                            data?.status
                              ?.toLowerCase()
                              .includes(word.toLowerCase())
    )
    );
    setSearchResult(result);
  };

  /**
 * @effect
 * @description On component mount, fetches all classes, sessions, and terms.
 */
  useEffect(() => {
    getAllSessions();
    getAllTerms();
  }, []);

  /**
 * @description When active_term_session changes, updates the selected term and fetches overview data.
 * @deps active_term_session
 */
  useEffect(() => {
    if (active_term_session?.id && selectedWard?.id) {
      setASelectedTerm(active_term_session?.id);
      getOverview(active_term_session?.id);
    }
  }, [active_term_session, selectedWard?.id]);

  /**
 * @effect
 * @description When activeSession changes, fetches all terms for the active session.
 */
  useEffect(() => {
    if (activeSession?.id) {
      // setSelectedSession(activeSession?.id);
      getAllTerms(activeSession?.id);
    }
  }, [activeSession]);

  return (
    <Layout children={
      <>
        <PageTitle
          title="Payment Overview"
          subtitle="View ward your payment overview."
        />

        <div className="flex gap-10 align-items-end margin-top-5 margin-bottom">
          <div>
            {activeSession?.id && (
              <SelectGroup
                children={
                  <select
                    defaultValue={activeSession?.id}
                    onChange={handleSessionChange}
                    name="session"
                  >
                    <option value="">---Select---</option>
                    {allSessions?.map((sesh) => {
                      return (
                        <option key={sesh?.id} value={sesh?.id}>
                          {sesh?.session}
                        </option>
                      );
                    })}
                  </select>
                }
              />
            )}
          </div>
          <div>
            {active_term_session?.id && (
              <SelectGroup
                children={
                  <select onChange={handleTermChange} name="term" value={aselectedTerm}>
                    <option value="">---Select---</option>
                    {selectedSessionTerms?.map((data) => {
                      return (
                        <option key={data?.id} value={data?.id}>
                          {data?.term}
                        </option>
                      );
                    })}
                  </select>
                }
              />
            )}
          </div>
        </div>
        <Grid container spacing={isMobile ? 1 : 3}>
          <Grid item lg={4} md={6} sm={6} xs={12}>
            <PaymentCard
              title="Total Fees"
              count={`${payemntOverview?.total_amount?.toLocaleString() || 0}`}
              icon={<Icon icon="material-symbols-light:payments-outline" />}
            />
          </Grid>
          <Grid item lg={4} md={6} sm={6} xs={12}>
            <PaymentCard
              title="Amount Paid"
              count={`${payemntOverview?.amount_paid?.toLocaleString() || 0}`}
              icon={<Icon icon="fluent:wallet-credit-card-48-filled" width="20" height="20" />}
            />
          </Grid>
          <Grid item lg={4} md={6} sm={6} xs={12}>
            <PaymentCard
              title="Outstanding Payments"
              count={`${payemntOverview
                ?.outstanding_amount
                ?.toLocaleString() || 0}`}
              icon={<Icon icon="hugeicons:wallet-remove-01" width="20" height="20" />}
            />
          </Grid>
        </Grid>

        <div className="page-content margin-top-30">
          <>
            {getIsLoading ? (
              <PageLoader />
            ) : (
              <Table
                data={searchResult}
                columns={columns}
                subHeader={true}
                pagination
                handleRowSelected={({ selectedRows }) => setSelectedRows(selectedRows.map(row => row.invoice_total))}
                subHeaderComponent={
                  <div className="action_div">
                    <TableSearch
                      placeholder="Search here..."
                      searchTableFunc={handleSearchStudent}
                    />

                    <div className="text-center gap-10 cursor-pointer">
                      <CSVExport
                        data={searchResult}
                        filename="Payment_overview_export"
                        idKey="invoice_total"
                        selectedRows={selectedRows}
                        fields={[
                          { path: "student_name", header: "Full Name" },
                          { path: "contact", header: "Phone Number" },
                          { path: "no_of_wards", header: "No of wards" },
                          { path: "is_active", header: "Active Status" }
                        ]}
                      />
                    </div>
                  </div>}
              />
            )}
          </>
        </div>
        {/* <ToastContainer /> */}
      </>
    }
    />
  );
};

export default AdminOverview;
